import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Loader2, Image, FileText, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { API, track } from "../api";
import { Modal, field, label } from "./Modal";
import { PremiumIcon } from "./PremiumIcon";
import { Consent } from "./Consent";

const ORDER = ["hogar", "mascotas", "acompanamiento", "recados"];

export const ProfessionalWizard = () => {
  const { t, lang } = useLang();
  const w = t.proWizard;
  const { proOpen, closePro } = useFlow();
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", last_name: "", phone: "", email: "", city: "Madrid", postal_code: "",
    categories: [], experience: "", languages: "", availability: "", hourly_rate: "", description: "",
  });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleCat = (c) => setForm((s) => ({ ...s, categories: s.categories.includes(c) ? s.categories.filter((x) => x !== c) : [...s.categories, c] }));
  const [bioLoading, setBioLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const genBio = async () => {
    setBioLoading(true);
    try {
      const { data } = await axios.post(`${API}/professionals/ai-bio`, { name: form.name, categories: form.categories, experience: form.experience, languages: form.languages, description: form.description, lang });
      if (data.bio) upd("description", data.bio);
    } catch (e) { setError(t.ai.error); }
    setBioLoading(false);
  };
  const [uploading, setUploading] = useState({ photo: false, cv: false });
  const [files, setFiles] = useState({ photo: "", cv: "" });
  const handleUpload = async (kind, file) => {
    if (!file) return;
    setError(""); setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(kind, data.path);
      setFiles((f) => ({ ...f, [kind]: data.original_filename }));
    } catch (e) {
      setError(e?.response?.data?.detail || w.error);
    }
    setUploading((u) => ({ ...u, [kind]: false }));
  };

  useEffect(() => { if (proOpen) { setStep(0); setStatus("idle"); setError(""); setConsent(false); setForm({ name: "", last_name: "", phone: "", email: "", city: "Madrid", postal_code: "", categories: [], experience: "", languages: "", availability: "", hourly_rate: "", description: "" }); setFiles({ photo: "", cv: "" }); } }, [proOpen]);

  const next = () => {
    setError("");
    if (step === 0 && (!form.name.trim() || !form.phone.trim() || !form.email.trim())) return setError(w.required);
    if (step === 1 && form.categories.length === 0) return setError(w.required);
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    setError("");
    if (!consent) return setError(t.common.privacyErr);
    setStatus("loading");
    try {
      await axios.post(`${API}/professionals`, { ...form, consent: true, lang });
      track("professional_registered", "");
      setStatus("success");
    } catch (e) {
      setStatus("idle");
      setError(e?.response?.status === 409 ? w.already : w.error);
    }
  };

  return (
    <Modal open={proOpen} onClose={closePro} testid="pro-wizard">
      {status === "success" ? (
        <div className="p-8 md:p-12 text-center" data-testid="pro-wizard-success">
          <div className="w-16 h-16 rounded-full bg-petrol text-white flex items-center justify-center mx-auto"><Check size={30} strokeWidth={3} /></div>
          <h3 className="mt-5 font-heading text-2xl md:text-3xl font-medium text-ink">{w.success.title}</h3>
          <p className="mt-3 text-ink-soft leading-relaxed max-w-md mx-auto">{w.success.body}</p>
          <button onClick={closePro} className="mt-8 inline-flex items-center justify-center rounded-full border border-line text-ink font-semibold px-6 py-3.5 hover:bg-cream transition-all" data-testid="pro-wizard-back">{w.success.back}</button>
        </div>
      ) : (
        <div className="p-6 md:p-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-petrol">{w.title}</span>
          <div className="mt-4 flex gap-1.5">
            {w.steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-petrol" : "bg-line"}`} />)}
          </div>
          <h3 className="mt-5 font-heading text-2xl md:text-3xl font-medium text-ink">{w.steps[step]}</h3>

          <div className="mt-6 min-h-[240px]">
            {step === 0 && (
              <div className="space-y-4" data-testid="pro-step-0">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.s1.name} *</label><input className={field} value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="pro-name" /></div>
                  <div><label className={label}>{w.s1.lastName}</label><input className={field} value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="pro-lastname" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.s1.phone} *</label><input className={field} value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="pro-phone" /></div>
                  <div><label className={label}>{w.s1.email} *</label><input type="email" className={field} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="pro-email" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.s1.city}</label><input className={field} value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="pro-city" /></div>
                  <div><label className={label}>{w.s1.postal}</label><input className={field} value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} data-testid="pro-postal" /></div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="pro-step-1">
                <p className="text-ink-soft mb-4">{w.s2.hint}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ORDER.map((key) => (
                    <button key={key} type="button" onClick={() => toggleCat(key)} className={`text-left rounded-2xl border p-5 transition-all duration-300 ${form.categories.includes(key) ? "border-petrol bg-cream ring-2 ring-petrol/20" : "border-line hover:border-petrol"}`} data-testid={`pro-cat-${key}`}>
                      <PremiumIcon category={key} active={form.categories.includes(key)} hoverGroup={false} size={22} className="!w-10 !h-10" />
                      <div className="mt-2 font-heading text-lg font-medium text-ink">{t.categories[key].label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="pro-step-2">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.s3.experience}</label><input className={field} value={form.experience} onChange={(e) => upd("experience", e.target.value)} data-testid="pro-experience" /></div>
                  <div><label className={label}>{w.s3.languages}</label><input className={field} value={form.languages} onChange={(e) => upd("languages", e.target.value)} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.s3.availability}</label><input className={field} value={form.availability} onChange={(e) => upd("availability", e.target.value)} /></div>
                  <div><label className={label}>{w.s3.rate}</label><input className={field} value={form.hourly_rate} onChange={(e) => upd("hourly_rate", e.target.value)} data-testid="pro-rate" /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={label + " !mb-0"}>{w.s3.description}</label>
                    <button type="button" onClick={genBio} disabled={bioLoading} className="inline-flex items-center gap-1.5 text-xs font-semibold text-petrol hover:underline disabled:opacity-50" data-testid="pro-ai-bio">
                      {bioLoading ? <><Loader2 size={13} className="animate-spin" /> {t.ai.bioLoading}</> : <><Sparkles size={13} /> {t.ai.bio}</>}
                    </button>
                  </div>
                  <textarea rows={4} className={field} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="pro-description" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="pro-step-3">
                <p className="text-ink-soft">{w.s4.note}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="cursor-pointer rounded-2xl border border-dashed border-line bg-cream/50 p-6 flex flex-col items-center text-center text-ink-faint hover:border-petrol transition-colors" data-testid="pro-photo-zone">
                    <Image size={26} className={files.photo ? "text-forest" : ""} />
                    <span className="mt-2 text-sm font-medium text-ink-soft">{w.s4.photo}</span>
                    <span className="mt-1 text-xs">{uploading.photo ? w.s4.uploading : files.photo ? w.s4.uploaded : w.s4.uploadPhoto}</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleUpload("photo", e.target.files?.[0])} data-testid="pro-photo-input" />
                  </label>
                  <label className="cursor-pointer rounded-2xl border border-dashed border-line bg-cream/50 p-6 flex flex-col items-center text-center text-ink-faint hover:border-petrol transition-colors" data-testid="pro-cv-zone">
                    <FileText size={26} className={files.cv ? "text-forest" : ""} />
                    <span className="mt-2 text-sm font-medium text-ink-soft">{w.s4.cv}</span>
                    <span className="mt-1 text-xs">{uploading.cv ? w.s4.uploading : files.cv ? w.s4.uploaded : w.s4.uploadCv}</span>
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload("cv", e.target.files?.[0])} data-testid="pro-cv-input" />
                  </label>
                </div>
                <div className="rounded-xl bg-beige/40 border border-line px-4 py-3 text-sm text-ink-soft font-medium">{w.s4.status}</div>
                <Consent checked={consent} onChange={setConsent} testid="pro-consent" />
              </div>
            )}
          </div>

          {error && <p className="mt-3 text-sm text-red-600" data-testid="pro-wizard-error">{error}</p>}

          <div className="mt-6 flex items-center justify-between gap-3">
            <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 rounded-full border border-line text-ink-soft font-semibold px-5 py-3 disabled:opacity-0 hover:bg-cream transition-all" data-testid="pro-wizard-back-btn"><ArrowLeft size={16} /> {w.back}</button>
            {step < 3 ? (
              <button onClick={next} className="group inline-flex items-center gap-2 rounded-full bg-petrol text-white font-semibold px-7 py-3.5 hover:opacity-90 transition-all hover:-translate-y-0.5" data-testid="pro-wizard-next">{w.next} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></button>
            ) : (
              <button onClick={submit} disabled={status === "loading"} className="inline-flex items-center gap-2 rounded-full bg-petrol text-white font-semibold px-7 py-3.5 hover:opacity-90 transition-all disabled:opacity-60" data-testid="pro-wizard-submit">{status === "loading" ? <><Loader2 size={16} className="animate-spin" /> {w.submitting}</> : w.submit}</button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
